// SPDX-License-Identifier: AGPL-3.0-or-later
//
// parley: the soundboard control in the voice bars (strand AJ,
// docs/strands/soundboard.md). It sits next to camera and screen share and
// behaves like them: one button, pressed state while its surface is open.
//
// Everything about the soundboard lives in here so that the two upstream
// bars (features/voice/components/VoiceControlBar.tsx and
// .../voice_connection_status/LocalParticipantControls.tsx) only carry one
// element each - the seam FORK.md asks for.
//
// Wide windows get a popout anchored above the button (focus trap, Escape
// and outside-click come from the popout layer), narrow ones the bottom
// sheet every other voice control uses there.

import {SOUNDBOARD_DESCRIPTOR} from '@app/parley/soundboard/SoundboardMessages';
import {SoundboardPanel} from '@app/parley/soundboard/SoundboardPanel';
import SoundboardStore from '@app/parley/soundboard/SoundboardStore';
import Permission from '@app/features/permissions/state/Permission';
import {ComponentBus} from '@app/features/platform/utils/ComponentBus';
import {BottomSheet} from '@app/features/ui/bottom_sheet/BottomSheet';
import * as PopoutCommands from '@app/features/ui/commands/PopoutCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {usePopout} from '@app/features/ui/hooks/usePopout';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {MusicNotesIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Fluxer's `voice_toggle_soundboard` keybind dispatches SOUNDBOARD_TOGGLE
 * and upstream leaves it unhandled. Both bars can be on screen at once, so
 * the shortcut goes to the control that mounted last - the one belonging
 * to the surface the user just opened.
 */
const mountedToggles: Array<() => void> = [];

export const SoundboardButton = observer(
	({
		popoutKey,
		className,
		activeClassName,
		iconClassName,
	}: {
		/** Unique per bar: both bars can be mounted at the same time. */
		popoutKey: string;
		className?: string;
		activeClassName?: string;
		iconClassName?: string;
	}): React.ReactElement | null => {
		const {i18n} = useLingui();
		const buttonRef = useRef<HTMLButtonElement>(null);
		const [sheetOpen, setSheetOpen] = useState(false);
		const {isOpen} = usePopout(popoutKey);
		const isMobile = MobileLayout.isMobileLayout();
		const channelId = MediaEngine.channelId;
		const guildId = MediaEngine.guildId;
		// Playing a sound is making noise in the channel, so it takes SPEAK -
		// the same permission the microphone takes (strand AG).
		const visible = Boolean(guildId && channelId) && Permission.can(Permissions.SPEAK, {channelId: channelId!});

		useEffect(() => {
			if (visible && guildId) void SoundboardStore.load(guildId);
		}, [visible, guildId]);

		const toggle = useCallback(() => {
			if (!guildId || !channelId) return;
			if (isMobile) {
				setSheetOpen((open) => !open);
				return;
			}
			const target = buttonRef.current;
			if (!target) return;
			if (isOpen) {
				PopoutCommands.close(popoutKey);
				return;
			}
			PopoutCommands.open({
				key: popoutKey,
				position: 'top-start',
				target,
				animationType: 'none',
				render: () => (
					<SoundboardPanel
						guildId={guildId}
						channelId={channelId}
						data-flx="parley.soundboard.soundboard-button.toggle.soundboard-panel"
					/>
				),
			});
		}, [channelId, guildId, isMobile, isOpen, popoutKey]);

		const toggleRef = useRef(toggle);
		toggleRef.current = toggle;
		useEffect(() => {
			if (!visible) return;
			const entry = () => toggleRef.current();
			mountedToggles.push(entry);
			const unsubscribe = ComponentBus.subscribe('SOUNDBOARD_TOGGLE', () => {
				mountedToggles[mountedToggles.length - 1]?.();
			});
			return () => {
				const index = mountedToggles.indexOf(entry);
				if (index > -1) mountedToggles.splice(index, 1);
				unsubscribe();
			};
		}, [visible]);

		if (!visible || !guildId || !channelId) return null;
		const label = i18n._(SOUNDBOARD_DESCRIPTOR);
		const active = isOpen || sheetOpen;
		return (
			<>
				<Tooltip text={label} data-flx="parley.soundboard.soundboard-button.tooltip">
					<FocusRing offset={-2} data-flx="parley.soundboard.soundboard-button.focus-ring">
						<button
							ref={buttonRef}
							type="button"
							className={clsx(className, active && activeClassName)}
							onClick={toggle}
							aria-label={label}
							aria-haspopup="dialog"
							aria-expanded={active}
							data-parley-soundboard-button="true"
							data-flx="parley.soundboard.soundboard-button.button.toggle"
						>
							<MusicNotesIcon
								weight="fill"
								className={iconClassName}
								data-flx="parley.soundboard.soundboard-button.music-notes-icon"
							/>
						</button>
					</FocusRing>
				</Tooltip>
				{isMobile && (
					<BottomSheet
						isOpen={sheetOpen}
						onClose={() => setSheetOpen(false)}
						title={label}
						disablePadding={true}
						data-flx="parley.soundboard.soundboard-button.bottom-sheet"
					>
						<SoundboardPanel
							guildId={guildId}
							channelId={channelId}
							variant="sheet"
							data-flx="parley.soundboard.soundboard-button.soundboard-panel"
						/>
					</BottomSheet>
				)}
			</>
		);
	},
);

export default SoundboardButton;
